import React from 'react';
import { motion } from 'framer-motion';
import { Quote, Sparkles, MessageCircle, Calendar, ListChecks, Star } from 'lucide-react';
export function Testimonials() {
  const testimonials = [{
    name: 'M.',
    meta: '34 años — Madrid',
    highlight: 'Por fin sentí que entendía mi cuerpo',
    text: 'Lo que más me gustó fue lo claro y ordenado que está todo. No es otro PDF lleno de teoría: es una ruta que se puede aplicar.'
  }, {
    name: 'Lucía R.',
    meta: '41 años — CDMX',
    highlight: 'Las recetas me devolvieron las ganas de cocinar',
    text: 'Son rápidas, ricas y realistas. Mi día a día cambió sin sentir que estoy a dieta.'
  }, {
    name: 'Andrea P.',
    meta: '38 años — Bogotá',
    highlight: 'El plan de 21 días me dio estructura',
    text: 'Tener las semanas separadas y los menús listos me quitó la ansiedad de decidir qué comer cada día.'
  }, {
    name: 'Camila',
    meta: '29 años — Buenos Aires',
    highlight: 'Una guía con tono femenino y cercano',
    text: 'No me sentí juzgada en ningún momento. Es como tener una amiga experta acompañándote.'
  }, {
    name: 'V.',
    meta: '45 años — Lima',
    highlight: 'Las listas de compra cambiaron mi semana',
    text: 'Ir al supermercado dejó de ser un caos. Todo está pensado para ahorrar tiempo.'
  }, {
    name: 'Sofía M.',
    meta: '37 años — Monterrey',
    highlight: 'Notar cambios reales sin volverme loca',
    text: 'Lo apliqué a mi ritmo. Pequeños cambios, gran diferencia en cómo me siento.'
  }];
  const trustBlocks = [{
    icon: Sparkles,
    title: 'Enfoque integral',
    desc: 'Cuerpo, hormonas, descanso y emociones — todo conectado.'
  }, {
    icon: MessageCircle,
    title: 'Lenguaje claro',
    desc: 'Sin tecnicismos. Como te explicaría una amiga experta.'
  }, {
    icon: Calendar,
    title: 'Plan de 21 días',
    desc: 'Estructura clara con menús, listas y rutinas listas.'
  }, {
    icon: ListChecks,
    title: 'Herramientas de seguimiento',
    desc: 'Tests, diario y autoevaluación incluidos.'
  }];
  const renderCard = (t: (typeof testimonials)[number], i: number) => <div className="bg-ivory p-7 rounded-2xl border border-taupe/10 shadow-[0_20px_60px_-40px_rgba(139,107,73,0.4)] relative h-full flex flex-col" data-id="element-317">
      <Quote className="w-9 h-9 text-clay/15 absolute top-5 right-5" data-id="element-318" />
      {/* Stars */}
      <div className="flex items-center gap-0.5 mb-3 text-clay" data-id="element-319">
        {[0, 1, 2, 3, 4].map(s => <Star key={s} className="w-3.5 h-3.5 fill-clay/80 text-clay/80" data-id="element-320" />)}
      </div>
      <h3 className="text-lg font-serif font-bold text-ink mb-3 pr-6 leading-snug" data-id="element-321">
        "{t.highlight}"
      </h3>
      <p className="text-ink/80 mb-6 text-sm leading-relaxed flex-1" data-id="element-322">
        {t.text}
      </p>
      <div data-id="element-323">
        <div className="text-sm font-bold text-taupe tracking-wider uppercase" data-id="element-324">
          {t.name}
        </div>
        <div className="text-xs text-muted/80 mt-0.5" data-id="element-325">{t.meta}</div>
      </div>
    </div>;
  return <section className="w-full px-4 py-20 bg-cream" data-id="element-326">
      <div className="max-w-6xl mx-auto" data-id="element-327">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-328">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-ink mb-6" data-id="element-329">
            Lo que dicen otras mujeres
          </h2>
        </motion.div>

        {/* Desktop / tablet grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6 mb-20" data-id="element-330">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} data-id="element-331">
              {renderCard(t, i)}
            </motion.div>)}
        </div>

        {/* Mobile swipeable carousel */}
        <div className="md:hidden -mx-4 mb-16" data-id="element-332">
          <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 px-4 pb-6 scrollbar-hide" style={{
          scrollbarWidth: 'none'
        }} data-id="element-333">
            {testimonials.map((t, i) => <div key={i} className="snap-center shrink-0 w-[85%]" data-id="element-334">
                {renderCard(t, i)}
              </div>)}
          </div>
          <p className="text-center text-xs text-muted/70 mt-1 tracking-wider uppercase" data-id="element-335">
            Desliza para ver más →
          </p>
        </div>

        {/* Mini-trust blocks */}
        <div className="max-w-5xl mx-auto" data-id="element-336">
          <h3 className="text-center text-2xl md:text-3xl font-serif text-ink mb-3" data-id="element-337">
            Por qué esta guía no es como las otras que has leído
          </h3>
          <p className="text-center text-base text-ink/70 mb-10 max-w-2xl mx-auto" data-id="element-338">
            Cuatro pilares que la convierten en una ruta clara, no en otro PDF
            que se queda guardado.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-339">
            {trustBlocks.map((block, i) => {
            const Icon = block.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 15
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.1
            }} className="flex flex-col items-center text-center gap-3 p-5 bg-ivory rounded-2xl border border-taupe/10" data-id="element-340">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-clay/10" data-id="element-341">
                    <Icon className="w-5 h-5 text-clay" data-id="element-342" />
                  </div>
                  <span className="text-base font-serif font-bold text-ink" data-id="element-343">
                    {block.title}
                  </span>
                  <span className="text-sm text-ink/70 leading-snug" data-id="element-344">
                    {block.desc}
                  </span>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}